/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import InfoDot from './components/InfoDot';
import { supabase } from './supabase';
import {
  DAYS, FULL_DAYS, PERIODS, PERIOD_ICONS, PERIOD_TIMES_DISPLAY,
  DEFAULT_PERIODS, ROLE_PRESETS,
  formatTime12, formatPhone,
  Counter, DaySelector, CopyMenu, TimeSelect, Icons,
} from './onboarding/shared';
import { getRegionFromZip } from './zipToRegion';
import BillingSettingsTab from './components/BillingSettingsTab';

const DEFAULT_STAGGER_OFFSETS = {
  'Cook': -60, 'Prep Cook': -90, 'Dishwasher': 30,
  'Server': -20, 'Bartender': -15, 'Host': -15,
  'Busser': -10, 'Manager': -30,
};

export default function Settings({ companyId }) {
  const [activeTab, setActiveTab] = useState('restaurant');
  // Billing tab needs auth.uid() for the billing-admin gate. Cheap one-shot
  // fetch on mount; companies.billing_admin_user_id pairs with this to
  // decide whether the operator sees billing controls or "Contact your
  // billing admin" copy. Both are passed down to BillingSettingsTab.
  const [currentUserId, setCurrentUserId] = useState(null);
  const [billingAdminUserId, setBillingAdminUserId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      setCurrentUserId(data?.user?.id || null);
    });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    supabase.from('companies').select('billing_admin_user_id').eq('id', companyId).maybeSingle().then(({ data }) => {
      if (cancelled) return;
      setBillingAdminUserId(data?.billing_admin_user_id || null);
    });
    return () => { cancelled = true; };
  }, [companyId]);
  const [saved, setSaved] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Restaurant info
  const [restaurant, setRestaurant] = useState({ name: '', manager: '', address: '', city: '', state: '', zip: '', phone: '', manager_phone: '', restaurant_type: '' });
  const [targetLabor, setTargetLabor] = useState(25);

  // Hours & periods
  const [dayData, setDayData] = useState(() =>
    DAYS.map(() => ({ isOpen: true, open: "11:00", close: "22:00", periods: JSON.parse(JSON.stringify(DEFAULT_PERIODS)) }))
  );
  const [selectedHoursDay, setSelectedHoursDay] = useState(0);
  const [hoursCardKey, setHoursCardKey] = useState(0);
  const [showHoursCopy, setShowHoursCopy] = useState(false);

  // Roles & staffing
  const [roles, setRoles] = useState(ROLE_PRESETS.map(r => ({ ...r, active: false })));
  const [staffing, setStaffing] = useState({});
  const [selectedStaffDay, setSelectedStaffDay] = useState(0);
  const [staffCardKey, setStaffCardKey] = useState(0);
  const [showStaffCopy, setShowStaffCopy] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleType, setNewRoleType] = useState('FOH');

  // Smart stagger
  const [staggerEnabled, setStaggerEnabled] = useState(true);
  const [staggerDisabledRoles, setStaggerDisabledRoles] = useState([]);
  const [lunchRush, setLunchRush] = useState('12:00');
  const [dinnerRush, setDinnerRush] = useState('18:00');
  const [brunchRush, setBrunchRush] = useState('');
  const [offsets, setOffsets] = useState(DEFAULT_STAGGER_OFFSETS);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (!companyId) return;
    loadSettings();
  }, [companyId]);

  async function loadSettings() {
    // Load company info
    const { data: companyData } = await supabase.from('companies').select('*').eq('id', companyId).single();
    if (companyData) {
      setRestaurant(prev => ({
        ...prev,
        name: companyData.name || '',
        manager: companyData.manager_name || '',
        address: companyData.address || '',
        city: companyData.city || '',
        state: companyData.state || '',
        zip: companyData.zip || '',
        phone: companyData.phone || '',
        manager_phone: companyData.manager_phone || '',
        restaurant_type: companyData.restaurant_type || '',
      }));
      setOvertimeWeeklyHours(Number(companyData.overtime_weekly_hours) || 40);
      setScheduleEndCodes(Array.isArray(companyData.schedule_end_codes) ? companyData.schedule_end_codes : []);
    }

    // Load restaurant hours
    const { data: hoursData } = await supabase.from('restaurant_hours').select('*').eq('company_id', companyId);
    if (hoursData && hoursData.length > 0) {
      const newDayData = [...dayData];
      hoursData.forEach(h => {
        const di = DAYS.indexOf(h.day_of_week);
        if (di >= 0) {
          newDayData[di] = { ...newDayData[di], isOpen: h.is_open, open: h.open_time || "11:00", close: h.close_time || "22:00" };
        }
      });
      setDayData(newDayData);
    }

    // Load service templates (meal periods)
    const { data: tplData } = await supabase.from('service_templates').select('*').eq('company_id', companyId);
    if (tplData && tplData.length > 0) {
      setDayData(prev => {
        const updated = [...prev];
        tplData.forEach(t => {
          const di = DAYS.indexOf(t.day_of_week);
          if (di >= 0 && updated[di].periods[t.meal_period] !== undefined) {
            updated[di] = {
              ...updated[di],
              periods: { ...updated[di].periods, [t.meal_period]: { ...updated[di].periods[t.meal_period], active: t.is_active } }
            };
          }
        });
        return updated;
      });
    }

    // Load roles — merge presets with any custom roles from DB
    const { data: rolesData } = await supabase.from('roles').select('*').eq('company_id', companyId).order('sort_order');
    if (rolesData && rolesData.length > 0) {
      const merged = ROLE_PRESETS.map(preset => {
        const found = rolesData.find(r => r.name === preset.name);
        return { ...preset, active: !!found };
      });
      // Add any custom roles not in presets
      rolesData.forEach(r => {
        if (!ROLE_PRESETS.find(p => p.name === r.name)) {
          merged.push({ name: r.name, type: r.type, color: r.color || '#64748B', active: true });
        }
      });
      setRoles(merged);
    }

    // Load staffing levels
    const { data: staffData } = await supabase.from('staffing_levels').select('*').eq('company_id', companyId);
    if (staffData) {
      const s = {};
      staffData.forEach(d => {
        const di = DAYS.indexOf(d.day_of_week);
        if (di >= 0) s[`${di}-${d.meal_period}-${d.role}`] = d.required_count;
      });
      setStaffing(s);
    }

    // Load smart stagger
    const { data: stagger } = await supabase.from('smart_stagger').select('*').eq('company_id', companyId).single();
    if (stagger) {
      setLunchRush(stagger.lunch_rush_time || '12:00');
      setDinnerRush(stagger.dinner_rush_time || '18:00');
      setBrunchRush(stagger.brunch_rush_time || '');
      if (stagger.role_offsets && Object.keys(stagger.role_offsets).length > 0) setOffsets(stagger.role_offsets);
      if (stagger.enabled !== undefined && stagger.enabled !== null) setStaggerEnabled(stagger.enabled);
      if (Array.isArray(stagger.disabled_roles)) setStaggerDisabledRoles(stagger.disabled_roles);
    }

    // Load opener/closer settings
    const { data: ocData } = await supabase.from('opener_closer_settings').select('*').eq('company_id', companyId);
    if (ocData && ocData.length > 0) {
      const oc = {};
      ocData.forEach(row => {
        // Rows with no meal_period are legacy day-level settings — fan out to all four possible periods
        // so the UI can read them back per period. Actual active periods are filtered at render time.
        const periods = row.meal_period ? [row.meal_period] : ['Breakfast', 'Lunch', 'Dinner', 'Late Night'];
        periods.forEach(p => {
          const key = `${DAYS.indexOf(row.day_of_week)}-${p}-${row.role}`;
          oc[key] = {
            has_openers: row.has_openers || false,
            opener_count: row.opener_count || 1,
            opener_mins_before_open: row.opener_mins_before_open || 30,
            has_closers: row.has_closers || false,
            closer_count: row.closer_count || 1,
            closer_mins_after_close: row.closer_mins_after_close || 30,
            end_mix: (row.end_mix && typeof row.end_mix === 'object') ? row.end_mix : {},
          };
        });
      });
      setOcSettings(oc);
    }
  }

  async function saveRestaurant() {
    const region = getRegionFromZip(restaurant.zip, restaurant.state);
    // Surface failures: silent error handling here previously let the field show "Austin Murray"
    // locally while the companies row stayed null on the server. Now we alert + skip the "Saved!"
    // confirmation so the user knows the write didn't land.
    const { error } = await supabase.from('companies').update({
      name: restaurant.name,
      manager_name: restaurant.manager,
      address: restaurant.address,
      city: restaurant.city,
      state: restaurant.state,
      zip: restaurant.zip,
      phone: restaurant.phone,
      manager_phone: restaurant.manager_phone,
      zip_code: restaurant.zip,
      region: region,
      restaurant_type: restaurant.restaurant_type || null,
    }).eq('id', companyId);
    if (error) {
      window.alert(`Save failed: ${error.message}`);
      return;
    }
    flashSaved();
  }

  async function saveHours() {
    // Save restaurant_hours
    const hoursRows = DAYS.map((day, i) => ({
      company_id: companyId,
      day_of_week: day,
      is_open: dayData[i].isOpen,
      open_time: dayData[i].open,
      close_time: dayData[i].close,
    }));
    await supabase.from('restaurant_hours').delete().eq('company_id', companyId);
    await supabase.from('restaurant_hours').insert(hoursRows);

    // Save service_templates
    const templateRows = [];
    DAYS.forEach((day, i) => {
      const d = dayData[i];
      Object.entries(d.periods).forEach(([period, config]) => {
        templateRows.push({
          company_id: companyId,
          day_of_week: day,
          meal_period: period,
          is_active: d.isOpen && config.active,
        });
      });
    });
    await supabase.from('service_templates').delete().eq('company_id', companyId);
    await supabase.from('service_templates').insert(templateRows);
    flashSaved();
  }

  async function saveRolesAndStaffing() {
    // Save roles
    const activeRoles = roles.filter(r => r.active);
    await supabase.from('roles').delete().eq('company_id', companyId);
    if (activeRoles.length > 0) {
      await supabase.from('roles').insert(activeRoles.map((r, i) => ({
        company_id: companyId,
        name: r.name,
        type: r.type,
        color: `bg-${r.name.toLowerCase().replace(' ', '-')}-100 text-${r.name.toLowerCase().replace(' ', '-')}-700`,
        sort_order: i,
      })));
    }

    // Save staffing levels
    const dayMap = { 0: 'Mon', 1: 'Tue', 2: 'Wed', 3: 'Thu', 4: 'Fri', 5: 'Sat', 6: 'Sun' };
    const staffRows = [];
    Object.entries(staffing).forEach(([key, count]) => {
      if (count > 0) {
        const [dayIndex, period, ...roleParts] = key.split('-');
        const role = roleParts.join('-');
        staffRows.push({
          company_id: companyId,
          day_of_week: dayMap[parseInt(dayIndex)],
          meal_period: period,
          role: role,
          required_count: count,
        });
      }
    });
    await supabase.from('staffing_levels').delete().eq('company_id', companyId);
    if (staffRows.length > 0) await supabase.from('staffing_levels').insert(staffRows);
    await saveOpenersClosers();
    flashSaved();
  }

  async function saveStagger() {
    await supabase.from('smart_stagger').upsert([{
      company_id: companyId,
      lunch_rush_time: lunchRush,
      dinner_rush_time: dinnerRush,
      brunch_rush_time: brunchRush || null,
      role_offsets: offsets,
      enabled: staggerEnabled,
      disabled_roles: staggerDisabledRoles,
    }], { onConflict: 'company_id' });
    flashSaved();
  }

  async function saveOpenersClosers() {
    await supabase.from('opener_closer_settings').delete().eq('company_id', companyId);
    const rows = [];
    Object.entries(ocSettings).forEach(([key, val]) => {
      const endMix = val.end_mix && typeof val.end_mix === 'object'
        ? Object.fromEntries(Object.entries(val.end_mix).filter(([, n]) => Number(n) > 0))
        : {};
      // Derive closer fields from end_mix.CL (Closers box was removed from the UI).
      const clCount = Number(endMix.CL) || 0;
      const hasClosers = clCount > 0;
      if (!val.has_openers && !hasClosers && Object.keys(endMix).length === 0) return;
      const parts = key.split('-');
      if (parts.length < 3) return;
      const di = parseInt(parts[0]);
      const period = parts[1];
      const role = parts.slice(2).join('-');
      if (isNaN(di) || di < 0 || di > 6) return;
      rows.push({
        company_id: companyId,
        day_of_week: DAYS[di],
        meal_period: period,
        role,
        has_openers: val.has_openers || false,
        opener_count: val.opener_count || 1,
        opener_mins_before_open: val.opener_mins_before_open || 30,
        has_closers: hasClosers,
        closer_count: hasClosers ? clCount : 1,
        closer_mins_after_close: val.closer_mins_after_close || 30,
        end_mix: endMix,
      });
    });
    if (rows.length > 0) await supabase.from('opener_closer_settings').insert(rows);
  }

  function flashSaved() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  async function saveSchedule() {
    const cleaned = scheduleEndCodes
      .map(ec => ({ code: (ec.code || '').trim().toUpperCase(), label: (ec.label || '').trim() }))
      .filter(ec => ec.code && ec.label);
    await supabase.from('companies').update({
      overtime_weekly_hours: Number(overtimeWeeklyHours) || 40,
      schedule_end_codes: cleaned,
    }).eq('id', companyId);
    setScheduleEndCodes(cleaned);
    flashSaved();
  }

  function getSaveHandler() {
    switch (activeTab) {
      case 'restaurant': return saveRestaurant;
      case 'hours': return saveHours;
      case 'roles': return saveRolesAndStaffing;
      case 'stagger': return saveStagger;
      case 'schedule': return saveSchedule;
      default: return saveRestaurant;
    }
  }

  // Hours helpers
  const setHoursField = (di, field, value) => {
    const u = [...dayData];
    u[di] = { ...u[di], [field]: value };
    setDayData(u);
  };
  const setHoursPeriod = (di, pn, field, value) => {
    const u = [...dayData];
    u[di] = { ...u[di], periods: { ...u[di].periods, [pn]: { ...u[di].periods[pn], [field]: value } } };
    setDayData(u);
  };
  const copyHoursDay = (targets) => {
    const u = [...dayData];
    const src = dayData[selectedHoursDay];
    targets.forEach(di => { if (di !== selectedHoursDay) u[di] = JSON.parse(JSON.stringify(src)); });
    setDayData(u);
  };

  // Roles & staffing helpers
  const activeRoles = roles.filter(r => r.active);
  const toggleRole = i => {
    const u = [...roles];
    u[i] = { ...u[i], active: !u[i].active };
    setRoles(u);
  };
  const removeRole = i => {
    const r = roles[i];
    // Clean up staffing entries for this role
    const cleaned = { ...staffing };
    Object.keys(cleaned).forEach(k => { if (k.endsWith(`-${r.name}`)) delete cleaned[k]; });
    setStaffing(cleaned);
    setRoles(roles.filter((_, idx) => idx !== i));
  };
  const addCustomRole = () => {
    if (!newRoleName.trim()) return;
    if (roles.find(r => r.name.toLowerCase() === newRoleName.trim().toLowerCase())) return;
    const colors = ['#E85D3A', '#D4742C', '#8B6914', '#2D8F5E', '#6C5CE7', '#0891B2', '#7C6B5A', '#64748B', '#993556', '#0F6E56'];
    const color = colors[roles.length % colors.length];
    setRoles([...roles, { name: newRoleName.trim(), type: newRoleType, color, active: true }]);
    setNewRoleName('');
  };

  // Get active periods for a given day index (from hours tab data)
  const getActivePeriodsForDay = (di) => {
    const dd = dayData[di];
    if (!dd || !dd.isOpen) return [];
    return Object.entries(dd.periods)
      .filter(([, config]) => config.active)
      .map(([name]) => name)
      .filter(name => PERIODS.includes(name)); // only show Breakfast/Lunch/Dinner in staffing
  };

  const setStaff = (k, v) => setStaffing(prev => ({ ...prev, [k]: v }));
  const activePeriods = getActivePeriodsForDay(selectedStaffDay);
  const copyStaffDay = targets => {
    const ns = { ...staffing };
    targets.forEach(di => {
      if (di !== selectedStaffDay) activePeriods.forEach(pr => activeRoles.forEach(r => {
        ns[`${di}-${pr}-${r.name}`] = staffing[`${selectedStaffDay}-${pr}-${r.name}`] || 0;
      }));
    });
    setStaffing(ns);
  };
  const totalStaff = Object.values(staffing).reduce((a, b) => a + b, 0);
  const staffDayTotal = activePeriods.reduce((s, pr) => s + activeRoles.reduce((a, r) => a + (staffing[`${selectedStaffDay}-${pr}-${r.name}`] || 0), 0), 0);

  // Restaurant info input helper
  const setField = (k, v) => setRestaurant(prev => ({ ...prev, [k]: v }));

  const d = dayData[selectedHoursDay];

  // Opener/closer state
  const [ocSettings, setOcSettings] = useState({});
  const [expandedStaffRows, setExpandedStaffRows] = useState({}); // key 'di-period-role' -> bool
  const [showAllStaffRoles, setShowAllStaffRoles] = useState(false);
  const [overtimeWeeklyHours, setOvertimeWeeklyHours] = useState(40);
  const [scheduleEndCodes, setScheduleEndCodes] = useState([]); // [{ code, label }]

  const tabs = [
    { id: 'restaurant', label: 'Restaurant' },
    { id: 'hours', label: 'Hours & Periods' },
    { id: 'roles', label: 'Staffing' },
    { id: 'stagger', label: 'Smart Stagger' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'email', label: 'Email forwarding' },
    { id: 'billing', label: 'Billing' },
  ];

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: '#F5F5F4', fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeSlide { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: isMobile ? '20px 16px' : '32px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', margin: 0, letterSpacing: '-0.02em' }}>Settings</h1>
            <p style={{ fontSize: 14, color: '#888', margin: '4px 0 0' }}>Configure your restaurant's schedule blueprint</p>
          </div>
          <button onClick={getSaveHandler()}
            style={{
              padding: '10px 24px', borderRadius: 10, border: 'none',
              background: saved ? '#2D8F5E' : '#1a1a1a',
              color: '#FFF', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s ease',
              fontFamily: "'DM Sans', sans-serif",
            }}>
            {saved ? '\u2713 Saved!' : `Save ${tabs.find(t => t.id === activeTab)?.label || 'changes'}`}
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, background: '#EBEBEB', borderRadius: 12, padding: 4, marginBottom: 28 }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: isMobile ? '10px 8px' : '10px 16px', borderRadius: 8, border: 'none',
                background: activeTab === tab.id ? '#FFF' : 'transparent',
                boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer', fontSize: isMobile ? 12 : 13, fontWeight: activeTab === tab.id ? 600 : 500,
                color: activeTab === tab.id ? '#1a1a1a' : '#999',
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all 0.2s ease',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ RESTAURANT TAB ═══ */}
        {activeTab === 'restaurant' && (
          <RestaurantTab restaurant={restaurant} setField={setField} isMobile={isMobile} targetLabor={targetLabor} setTargetLabor={setTargetLabor} />
        )}

        {/* ═══ HOURS & PERIODS TAB ═══ */}
        {activeTab === 'hours' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '0 4px' }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', margin: 0, fontFamily: "'DM Sans',sans-serif" }}>
                {FULL_DAYS[selectedHoursDay]}
                {d.isOpen && <span style={{ fontSize: 13, fontWeight: 400, color: '#999', marginLeft: 8 }}>{formatTime12(d.open)} – {formatTime12(d.close)}</span>}
                {!d.isOpen && <span style={{ fontSize: 13, fontWeight: 500, color: '#E85D3A', marginLeft: 8 }}>Closed</span>}
              </h3>
              <CopyMenu label={DAYS[selectedHoursDay]} onCopy={copyHoursDay} show={showHoursCopy} setShow={setShowHoursCopy} isMobile={isMobile} dayData={dayData} selectedDay={selectedHoursDay} />
            </div>

            <DaySelector selectedDay={selectedHoursDay} onSelect={di => { setSelectedHoursDay(di); setHoursCardKey(k => k + 1); }} isMobile={isMobile} renderBadge={di => {
              const dx = dayData[di];
              if (!dx.isOpen) return <span style={{ fontSize: 10, color: di === selectedHoursDay ? 'rgba(255,255,255,0.5)' : '#CCC', fontFamily: "'DM Sans',sans-serif", textDecoration: 'line-through' }}>Off</span>;
              return <span style={{ fontSize: 10, fontWeight: 600, color: di === selectedHoursDay ? 'rgba(255,255,255,0.55)' : '#BBB', fontFamily: "'DM Sans',sans-serif" }}>{formatTime12(dx.open).replace(/:00/g, '').replace(' ', '')}</span>;
            }} />

            <div key={hoursCardKey} style={{ marginTop: 12, animation: 'fadeSlide 0.25s ease-out' }}>
              <div style={{ background: '#FFF', borderRadius: 16, padding: isMobile ? '16px' : '22px 28px', border: '1px solid #EBEBEB', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: d.isOpen ? 20 : 0, paddingBottom: d.isOpen ? 16 : 0, borderBottom: d.isOpen ? '1px solid #F3F3F3' : 'none' }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif" }}>Restaurant Hours</div>
                  <button onClick={() => setHoursField(selectedHoursDay, 'isOpen', !d.isOpen)} style={{ width: 44, height: 26, borderRadius: 13, border: 'none', background: d.isOpen ? '#2D8F5E' : '#DDD', cursor: 'pointer', position: 'relative', WebkitTapHighlightColor: 'transparent' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#FFF', position: 'absolute', top: 3, left: d.isOpen ? 21 : 3, transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
                  </button>
                </div>
                {d.isOpen && <>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                    <TimeSelect value={d.open} onChange={v => setHoursField(selectedHoursDay, 'open', v)} isMobile={isMobile} label="Opens at" />
                    <TimeSelect value={d.close} onChange={v => setHoursField(selectedHoursDay, 'close', v)} isMobile={isMobile} label="Closes at" />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", marginBottom: 10 }}>Meal Periods</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Object.keys(d.periods).map(pn => (
                      <PeriodToggle key={pn} name={pn} active={d.periods[pn].active} start={d.periods[pn].start} end={d.periods[pn].end}
                        onToggle={() => setHoursPeriod(selectedHoursDay, pn, 'active', !d.periods[pn].active)}
                        onStart={v => setHoursPeriod(selectedHoursDay, pn, 'start', v)}
                        onEnd={v => setHoursPeriod(selectedHoursDay, pn, 'end', v)}
                        isMobile={isMobile} />
                    ))}
                  </div>
                </>}
              </div>
            </div>
          </div>
        )}

        {/* ═══ ROLES & STAFFING TAB ═══ */}
        {activeTab === 'roles' && (
          <div>
            {/* Role Selection */}
            <div style={{ background: '#FFF', borderRadius: 16, padding: isMobile ? '16px' : '20px 28px', marginBottom: isMobile ? 16 : 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #EBEBEB' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', margin: 0 }}>Your Roles</h3>
                {activeRoles.length > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: '#2D8F5E', background: '#2D8F5E10', padding: '4px 10px', borderRadius: 6 }}>{activeRoles.length} active</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                {roles.map((r, i) => (
                  <div key={r.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 0, borderRadius: 20, border: r.active ? `2px solid ${r.color}` : '2px solid #E5E5E5', background: r.active ? `${r.color}0D` : '#FFF', transition: 'all 0.2s ease', overflow: 'hidden' }}>
                    <button onClick={() => toggleRole(i)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: isMobile ? '10px 10px 10px 14px' : '8px 10px 8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 13, fontWeight: 500, color: r.active ? r.color : '#888', WebkitTapHighlightColor: 'transparent' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.active ? r.color : '#CCC' }} />{r.name}<span style={{ fontSize: 10, color: r.active ? r.color + '99' : '#BBB', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{r.type}</span>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove "${r.name}" role? This will also clear its staffing data.`)) removeRole(i); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: '100%', border: 'none', background: 'transparent', cursor: 'pointer', color: '#CCC', padding: '0 8px 0 0', transition: 'color 0.15s ease', WebkitTapHighlightColor: 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#E85D3A'}
                      onMouseLeave={e => e.currentTarget.style.color = '#CCC'}
                      title={`Remove ${r.name}`}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
              </div>
              {/* Add new role */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 12, borderTop: '1px solid #F0F0F0' }}>
                <input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="New role name"
                  onKeyDown={e => { if (e.key === 'Enter') addCustomRole(); }}
                  style={{ flex: 2, padding: '9px 14px', borderRadius: 10, border: '2px solid #E5E5E5', background: '#FAFAFA', fontSize: 13, fontWeight: 500, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", outline: 'none', boxSizing: 'border-box' }} />
                <select value={newRoleType} onChange={e => setNewRoleType(e.target.value)}
                  style={{ padding: '9px 12px', borderRadius: 10, border: '2px solid #E5E5E5', background: '#FAFAFA', fontSize: 13, fontWeight: 500, color: '#333', fontFamily: "'DM Sans',sans-serif", outline: 'none', appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}>
                  <option value="FOH">FOH</option>
                  <option value="BOH">BOH</option>
                </select>
                <button onClick={addCustomRole} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: '2px dashed #DDD', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#999', fontFamily: "'DM Sans',sans-serif", transition: 'all 0.15s ease', whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#1a1a1a'; e.currentTarget.style.color = '#1a1a1a'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#DDD'; e.currentTarget.style.color = '#999'; }}>
                  + Add
                </button>
              </div>
            </div>

            {/* Staffing per day */}
            {activeRoles.length > 0 && <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '0 4px' }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', margin: 0, fontFamily: "'DM Sans',sans-serif" }}>
                  {FULL_DAYS[selectedStaffDay]}{staffDayTotal > 0 && <span style={{ fontSize: 13, fontWeight: 400, color: '#999', marginLeft: 8 }}>{staffDayTotal} staff</span>}
                  {!dayData[selectedStaffDay]?.isOpen && <span style={{ fontSize: 13, fontWeight: 500, color: '#E85D3A', marginLeft: 8 }}>Closed</span>}
                </h3>
                <CopyMenu label={DAYS[selectedStaffDay]} onCopy={copyStaffDay} show={showStaffCopy} setShow={setShowStaffCopy} isMobile={isMobile} dayData={dayData} selectedDay={selectedStaffDay} />
              </div>

              <DaySelector selectedDay={selectedStaffDay} onSelect={di => { setSelectedStaffDay(di); setStaffCardKey(k => k + 1); }} isMobile={isMobile} renderBadge={di => {
                const dayPeriods = getActivePeriodsForDay(di);
                const dt = dayPeriods.reduce((s, pr) => s + activeRoles.reduce((a, r) => a + (staffing[`${di}-${pr}-${r.name}`] || 0), 0), 0);
                if (!dayData[di]?.isOpen) return <span style={{ fontSize: 10, color: di === selectedStaffDay ? 'rgba(255,255,255,0.5)' : '#CCC', fontFamily: "'DM Sans',sans-serif", textDecoration: 'line-through' }}>Off</span>;
                if (dt > 0 && di !== selectedStaffDay) return <><span style={{ fontSize: 11, fontWeight: 700, color: '#BBB', fontFamily: "'DM Sans',sans-serif" }}>{dt}</span><div style={{ position: 'absolute', bottom: 4, width: 4, height: 4, borderRadius: '50%', background: '#2D8F5E' }} /></>;
                if (dt > 0 && di === selectedStaffDay) return <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', fontFamily: "'DM Sans',sans-serif" }}>{dt}</span>;
                return null;
              }} />

              {activePeriods.length === 0 ? (
                <div style={{ background: '#FFF', borderRadius: 16, padding: '40px 20px', border: '1px solid #EBEBEB', textAlign: 'center', marginTop: 12 }}>
                  <div style={{ fontSize: 14, color: '#BBB', fontFamily: "'DM Sans',sans-serif" }}>
                    {!dayData[selectedStaffDay]?.isOpen
                      ? 'This day is closed. Open it on the Hours & Periods tab.'
                      : 'No meal periods active for this day. Enable them on the Hours & Periods tab.'}
                  </div>
                </div>
              ) : (
                <div key={staffCardKey} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 16, marginTop: 12, animation: 'fadeSlide 0.25s ease-out' }}>
                  {activePeriods.map((period, pi) => {
                    const pt = activeRoles.reduce((s, r) => s + (staffing[`${selectedStaffDay}-${period}-${r.name}`] || 0), 0);
                    const isFirstPeriod = pi === 0;
                    return (
                      <div key={period} style={{ background: '#FFF', borderRadius: 16, padding: isMobile ? '14px 16px' : '18px 22px', border: '1px solid #EBEBEB', boxShadow: '0 1px 4px rgba(0,0,0,0.03)', flex: isMobile ? 'none' : '1 1 0', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #F3F3F3' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#F7F7F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{PERIOD_ICONS[period]}</div>
                            <div>
                              <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif" }}>{period}</div>
                              <div style={{ fontSize: 11, color: '#AAA', fontFamily: "'DM Sans',sans-serif" }}>{PERIOD_TIMES_DISPLAY[period]}</div>
                            </div>
                          </div>
                          {pt > 0 && <div style={{ background: '#F0F0F0', borderRadius: 8, padding: '5px 12px', fontSize: 13, fontWeight: 700, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif" }}>{pt}</div>}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 6 }}>
                          {activeRoles
                            .filter(role => {
                              // Hide roles with zero staffing for this period unless the user toggled "Show all roles".
                              if (showAllStaffRoles) return true;
                              const v = staffing[`${selectedStaffDay}-${period}-${role.name}`] || 0;
                              return v > 0;
                            })
                            .map(role => {
                            const k = `${selectedStaffDay}-${period}-${role.name}`, v = staffing[k] || 0;
                            const ocKey = `${selectedStaffDay}-${period}-${role.name}`;
                            const oc = ocSettings[ocKey] || { has_openers: false, opener_count: 1, opener_mins_before_open: 30, has_closers: false, closer_count: 1, closer_mins_after_close: 30, end_mix: {} };
                            const setOcField = (field, value) => setOcSettings(prev => ({ ...prev, [ocKey]: { ...oc, [field]: value } }));
                            const expanded = !!expandedStaffRows[ocKey];
                            const mixTotal = Object.values(oc.end_mix || {}).reduce((a, n) => a + (Number(n) || 0), 0);
                            const showOpeners = isFirstPeriod && oc.has_openers;
                            const hasDetails = showOpeners || mixTotal > 0;
                            return (
                              <div key={role.name} style={{ borderRadius: 10, background: v > 0 ? `${role.color}06` : '#FAFAFA', overflow: 'hidden' }}>
                                <div onClick={() => v > 0 && setExpandedStaffRows(p => ({ ...p, [ocKey]: !p[ocKey] }))}
                                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '8px 10px' : '6px 10px', cursor: v > 0 ? 'pointer' : 'default' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 8, minWidth: 0 }}>
                                    <div style={{ width: 4, height: 24, borderRadius: 2, background: v > 0 ? role.color : '#E5E5E5' }} />
                                    <span style={{ fontSize: isMobile ? 15 : 14, color: v > 0 ? '#333' : '#AAA', fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>{role.name}</span>
                                    {hasDetails && v > 0 && (() => {
                                      const mixDisplay = Object.entries(oc.end_mix || {})
                                        .filter(([, n]) => Number(n) > 0)
                                        .map(([c, n]) => `${n}${c}`).join(' ');
                                      return (
                                        <span style={{ fontSize: 10, color: '#999', fontFamily: "'DM Sans',sans-serif" }}>
                                          {showOpeners ? ` · ${oc.opener_count}O` : ''}
                                          {mixDisplay ? ` · ${mixDisplay}` : ''}
                                        </span>
                                      );
                                    })()}
                                    {v > 0 && <span style={{ fontSize: 11, color: '#BBB', marginLeft: 'auto' }}>{expanded ? '▾' : '▸'}</span>}
                                  </div>
                                  <div onClick={e => e.stopPropagation()}>
                                    <Counter value={v} onChange={nv => setStaff(k, nv)} color={role.color} isMobile={isMobile} />
                                  </div>
                                </div>
                                {expanded && v > 0 && (
                                  <div style={{ padding: '10px 12px 12px', background: '#FFF', borderTop: '1px solid #F3F3F3', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {isFirstPeriod && (
                                      /* Openers — only shown for the first active period of the day. */
                                      <div style={{ padding: 10, borderRadius: 10, background: oc.has_openers ? '#FFFBEB' : '#FAFAFA', border: oc.has_openers ? '1px solid #FDE68A' : '1px solid #F0F0F0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: oc.has_openers ? '#92400E' : '#999', fontFamily: "'DM Sans',sans-serif" }}>Openers</span>
                                            {oc.has_openers && (
                                              <>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                  <span style={{ fontSize: 11, color: '#666' }}>Count</span>
                                                  <Counter value={oc.opener_count} onChange={val => setOcField('opener_count', val)} min={1} max={Math.max(v, 5)} isMobile={isMobile} color="#F59E0B" />
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                  <span style={{ fontSize: 11, color: '#666' }}>Arrive</span>
                                                  <Counter value={oc.opener_mins_before_open} onChange={val => setOcField('opener_mins_before_open', val)} min={0} max={180} isMobile={isMobile} color="#F59E0B" />
                                                  <span style={{ fontSize: 10, color: '#999', whiteSpace: 'nowrap' }}>min before</span>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                          <button onClick={() => setOcField('has_openers', !oc.has_openers)} style={{ width: 32, height: 18, borderRadius: 9, border: 'none', background: oc.has_openers ? '#F59E0B' : '#DDD', cursor: 'pointer', position: 'relative' }}>
                                            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#FFF', position: 'absolute', top: 3, left: oc.has_openers ? 17 : 3, transition: 'left 0.2s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
                                          </button>
                                        </div>
                                        {oc.has_openers && (
                                          <div style={{ fontSize: 10, color: '#92400E', marginTop: 6, fontStyle: 'italic' }}>
                                            {scheduleEndCodes.some(ec => ec.code === 'BD') ? 'Openers auto-get a BD end code by default.' : 'Add a BD end code in Schedule settings to auto-label openers.'}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {/* End Mix */}
                                    <div style={{ padding: 10, borderRadius: 10, background: '#F0FDFA', border: '1px solid #CCFBF1' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: scheduleEndCodes.length > 0 ? 8 : 0 }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#115E59', fontFamily: "'DM Sans',sans-serif" }}>End mix</span>
                                        {(() => {
                                          const totalAssigned = Object.values(oc.end_mix || {}).reduce((a, n) => a + (Number(n) || 0), 0);
                                          return <span style={{ fontSize: 10, color: '#5EAD9E' }}>{totalAssigned} of {v} assigned{totalAssigned > v ? ' (over)' : ''}</span>;
                                        })()}
                                      </div>
                                      {scheduleEndCodes.length === 0 ? (
                                        <div style={{ fontSize: 11, color: '#5EAD9E', fontStyle: 'italic' }}>Add end codes in the Schedule tab to use this.</div>
                                      ) : (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                          {scheduleEndCodes.map(ec => {
                                            const currentCount = Number((oc.end_mix || {})[ec.code]) || 0;
                                            return (
                                              <div key={ec.code}
                                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 8, background: '#FFF', border: '1px solid #CCFBF1' }}>
                                                <span style={{ fontSize: 10, fontWeight: 700, color: '#115E59', letterSpacing: '0.03em', padding: '2px 5px', borderRadius: 3, background: '#CCFBF1' }}>{ec.code}</span>
                                                <span style={{ fontSize: 11, color: '#666' }}>{ec.label}</span>
                                                <Counter value={currentCount} onChange={val => setOcField('end_mix', { ...(oc.end_mix || {}), [ec.code]: val })} min={0} max={Math.max(v, 10)} isMobile={isMobile} color="#0D9488" />
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {!showAllStaffRoles && activeRoles.every(r => (staffing[`${selectedStaffDay}-${period}-${r.name}`] || 0) === 0) && (
                            <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', padding: '8px 10px' }}>No roles staffed yet. Click "Show all roles" to add some.</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {activePeriods.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowAllStaffRoles(v => !v)}
                    style={{ fontSize: 12, fontWeight: 500, color: '#4B5563', background: '#FFF', border: '1px solid #EBEBEB', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                    {showAllStaffRoles ? 'Hide zero-count roles' : '+ Show all roles'}
                  </button>
                </div>
              )}

              {/* Summary */}
              <div style={{ background: '#FFF', borderRadius: 16, padding: isMobile ? '14px 16px' : '18px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #EBEBEB', display: 'flex', alignItems: 'center', justifyContent: 'space-around', marginTop: isMobile ? 16 : 24 }}>
                {[{ l: 'Shifts / Week', v: totalStaff }, { l: 'Roles', v: activeRoles.length }, { l: 'Avg / Day', v: totalStaff > 0 ? Math.round(totalStaff / 7) : 0 }].map(({ l, v }) =>
                  <div key={l} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'DM Sans',sans-serif" }}>{l}</div>
                    <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#1a1a1a', marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>{v}</div>
                  </div>
                )}
              </div>
            </>}
          </div>
        )}


        {/* ═══ SMART STAGGER TAB ═══ */}
        {activeTab === 'stagger' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: staggerEnabled ? 1 : 0.6, transition: 'opacity 0.3s ease' }}>
            {/* Master toggle */}
            <div style={{ background: '#FFF', borderRadius: 16, border: '1px solid #EBEBEB', padding: isMobile ? '16px' : '20px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}>
                    Smart Stagger
                    <InfoDot>Offsets shift start times so the whole team doesn't arrive at once. The first person in each role starts at the period default, others arrive a few minutes later based on your rush times and per-role offsets.</InfoDot>
                  </div>
                  <div style={{ fontSize: 13, color: '#999', fontFamily: "'DM Sans',sans-serif", marginTop: 2 }}>
                    {staggerEnabled ? 'Shift start times are offset from rush times' : 'All shifts start at the period default time'}
                  </div>
                </div>
                <button onClick={() => setStaggerEnabled(!staggerEnabled)} style={{ width: 44, height: 26, borderRadius: 13, border: 'none', background: staggerEnabled ? '#2D8F5E' : '#DDD', cursor: 'pointer', position: 'relative', transition: 'background 0.2s ease', WebkitTapHighlightColor: 'transparent' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#FFF', position: 'absolute', top: 3, left: staggerEnabled ? 21 : 3, transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
                </button>
              </div>
              {staggerEnabled && (
                <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, background: '#F7F7F6', border: '1px solid #EBEBEB', fontSize: 12, color: '#888', fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
                  Stagger only applies after confirming at least one person is scheduled at the start of each shift. The first employee always starts at the period default time.
                </div>
              )}
            </div>

            {staggerEnabled && <>
              {/* Rush Times */}
              <div style={{ background: '#FFF', borderRadius: 16, border: '1px solid #EBEBEB', padding: isMobile ? '16px' : '22px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, fontFamily: "'DM Sans',sans-serif" }}>Rush Times</div>
                <div style={{ fontSize: 13, color: '#BBB', marginBottom: 16, fontFamily: "'DM Sans',sans-serif" }}>When does your restaurant start getting busy?</div>
                <div style={{ display: 'flex', gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
                  {[
                    { label: 'Lunch rush', value: lunchRush, onChange: setLunchRush },
                    { label: 'Dinner rush', value: dinnerRush, onChange: setDinnerRush },
                    { label: 'Brunch rush (optional)', value: brunchRush, onChange: setBrunchRush },
                  ].map(({ label, value, onChange }) => (
                    <div key={label} style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, fontFamily: "'DM Sans',sans-serif" }}>{label}</div>
                      <input type="time" value={value} onChange={e => onChange(e.target.value)}
                        style={{ width: '100%', border: '1px solid #E5E5E5', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontFamily: "'DM Sans',sans-serif", color: '#333', background: '#FAFAFA', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Role Offsets with per-role disable */}
              <div style={{ background: '#FFF', borderRadius: 16, border: '1px solid #EBEBEB', padding: isMobile ? '16px' : '22px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, fontFamily: "'DM Sans',sans-serif" }}>Role Start Offsets</div>
                <div style={{ fontSize: 13, color: '#BBB', marginBottom: 16, fontFamily: "'DM Sans',sans-serif" }}>Minutes before/after rush time. Toggle off to use period default time for that role.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {activeRoles.map(role => {
                    const isDisabled = staggerDisabledRoles.includes(role.name);
                    return (
                      <div key={role.name} style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: isDisabled ? 0.4 : 1, transition: 'opacity 0.2s ease' }}>
                        <button onClick={() => setStaggerDisabledRoles(prev => isDisabled ? prev.filter(n => n !== role.name) : [...prev, role.name])}
                          style={{ width: 32, height: 20, borderRadius: 10, border: 'none', background: isDisabled ? '#DDD' : '#2D8F5E', cursor: 'pointer', position: 'relative', transition: 'background 0.2s ease', WebkitTapHighlightColor: 'transparent', flexShrink: 0 }}>
                          <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#FFF', position: 'absolute', top: 3, left: isDisabled ? 3 : 15, transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
                        </button>
                        <div style={{ width: 90, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <div style={{ width: 4, height: 20, borderRadius: 2, background: role.color }} />
                          <span style={{ fontSize: 13, fontWeight: 500, color: '#333', fontFamily: "'DM Sans',sans-serif" }}>{role.name}</span>
                        </div>
                        <input type="range" min="-120" max="120" step="5"
                          value={offsets[role.name] ?? 0}
                          onChange={e => setOffsets(prev => ({ ...prev, [role.name]: parseInt(e.target.value) }))}
                          disabled={isDisabled}
                          style={{ flex: 1 }} />
                        <span style={{
                          fontSize: 12, fontWeight: 600, width: 100, textAlign: 'right',
                          color: isDisabled ? '#CCC' : (offsets[role.name] ?? 0) < 0 ? '#2D8F5E' : (offsets[role.name] ?? 0) > 0 ? '#E85D3A' : '#BBB',
                          fontFamily: "'DM Sans',sans-serif",
                        }}>
                          {isDisabled ? 'Default' : (offsets[role.name] ?? 0) === 0 ? 'At rush' : (offsets[role.name] ?? 0) < 0 ? `${Math.abs(offsets[role.name])}m before` : `${offsets[role.name]}m after`}
                        </span>
                      </div>
                    );
                  })}
                  {activeRoles.length === 0 && (
                    <div style={{ fontSize: 13, color: '#CCC', fontFamily: "'DM Sans',sans-serif", textAlign: 'center', padding: 20 }}>Select roles in the Roles & Staffing tab first</div>
                  )}
                </div>
              </div>
            </>}
          </div>
        )}

        {/* ═══ SCHEDULE TAB ═══ */}
        {activeTab === 'schedule' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Overtime threshold */}
            <div style={{ background: '#FFF', borderRadius: 16, border: '1px solid #EBEBEB', padding: isMobile ? '16px' : '20px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}>
                Overtime threshold
                <InfoDot>Any weekly hours above this number are paid at 1.5× the employee's hourly rate and flagged in red on the schedule.</InfoDot>
              </div>
              <div style={{ fontSize: 13, color: '#999', fontFamily: "'DM Sans',sans-serif", marginTop: 2, marginBottom: 14 }}>Weekly hours past this number turn red on the schedule and are paid at 1.5× the employee's rate.</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="number" min="1" max="168" value={overtimeWeeklyHours} onChange={e => setOvertimeWeeklyHours(e.target.value)}
                  style={{ width: 80, padding: '8px 10px', borderRadius: 8, border: '1px solid #DDD', fontSize: 14, fontFamily: "'DM Sans',sans-serif" }} />
                <span style={{ fontSize: 13, color: '#666' }}>hours per week</span>
              </div>
            </div>

            {/* End codes */}
            <div style={{ background: '#FFF', borderRadius: 16, border: '1px solid #EBEBEB', padding: isMobile ? '16px' : '20px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}>
                End codes
                <InfoDot>Short labels that show in place of an end time on the schedule. Example: "4pm–BD" means they start at 4pm and leave on business decline. The real end time is still stored for labor cost math.</InfoDot>
              </div>
              <div style={{ fontSize: 13, color: '#999', fontFamily: "'DM Sans',sans-serif", marginTop: 2, marginBottom: 14 }}>
                Short labels shown in place of an end time (e.g. "4pm – BD"). The shift still stores an end time behind the scenes so labor cost stays accurate.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {scheduleEndCodes.map((ec, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input value={ec.code} onChange={e => { const u = [...scheduleEndCodes]; u[i] = { ...u[i], code: e.target.value.toUpperCase().slice(0, 6) }; setScheduleEndCodes(u); }}
                      placeholder="CL" maxLength={6}
                      style={{ width: 70, padding: '8px 10px', borderRadius: 8, border: '1px solid #DDD', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', fontFamily: "'DM Sans',sans-serif" }} />
                    <input value={ec.label} onChange={e => { const u = [...scheduleEndCodes]; u[i] = { ...u[i], label: e.target.value }; setScheduleEndCodes(u); }}
                      placeholder="Close"
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #DDD', fontSize: 13, fontFamily: "'DM Sans',sans-serif" }} />
                    <button onClick={() => setScheduleEndCodes(scheduleEndCodes.filter((_, j) => j !== i))}
                      style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #EEE', background: '#FAFAFA', color: '#999', cursor: 'pointer', fontSize: 13 }}>Remove</button>
                  </div>
                ))}
                <button onClick={() => setScheduleEndCodes([...scheduleEndCodes, { code: '', label: '' }])}
                  style={{ marginTop: 4, alignSelf: 'flex-start', padding: '8px 14px', borderRadius: 8, border: '1px dashed #CCC', background: 'transparent', color: '#666', cursor: 'pointer', fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>
                  + Add end code
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ EMAIL FORWARDING TAB ═══ */}
        {activeTab === 'email' && (
          <EmailForwardingTab companyId={companyId} isMobile={isMobile} />
        )}
        {activeTab === 'billing' && (
          <BillingSettingsTab
            companyId={companyId}
            currentUserId={currentUserId}
            billingAdminUserId={billingAdminUserId}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
}

/* ── Period Toggle ── */
function PeriodToggle({ name, active, start, end, onToggle, onStart, onEnd, isMobile }) {
  return (
    <div style={{ padding: isMobile ? '12px 14px' : '12px 16px', borderRadius: 12, background: active ? '#FAFAFA' : '#FDFDFD', border: active ? '1px solid #E5E5E5' : '1px solid #F0F0F0', opacity: active ? 1 : 0.5, transition: 'all 0.2s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: active ? 12 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>{PERIOD_ICONS[name]}</span>
          <span style={{ fontSize: isMobile ? 15 : 14, fontWeight: 600, color: active ? '#1a1a1a' : '#999', fontFamily: "'DM Sans',sans-serif" }}>{name}</span>
        </div>
        <button onClick={onToggle} style={{ width: 44, height: 26, borderRadius: 13, border: 'none', background: active ? '#2D8F5E' : '#DDD', cursor: 'pointer', position: 'relative', transition: 'background 0.2s ease', WebkitTapHighlightColor: 'transparent' }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#FFF', position: 'absolute', top: 3, left: active ? 21 : 3, transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
        </button>
      </div>
      {active && <div style={{ display: 'flex', gap: 12 }}><TimeSelect value={start} onChange={onStart} isMobile={isMobile} label="Start" /><TimeSelect value={end} onChange={onEnd} isMobile={isMobile} label="End" /></div>}
    </div>
  );
}

/* ── Restaurant Info Tab (wizard style) ── */
function RestaurantTab({ restaurant, setField, isMobile, targetLabor, setTargetLabor }) {
  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div style={{ background: '#FFF', borderRadius: 16, padding: isMobile ? '20px 18px' : '28px 32px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #EBEBEB' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 18 : 16 }}>
          <WizardInput label="Restaurant Name" value={restaurant.name} onChange={v => setField('name', v)} placeholder="e.g. The Golden Fork" isMobile={isMobile} icon={Icons.store} />
          <WizardInput label="Manager Name" value={restaurant.manager} onChange={v => setField('manager', v)} placeholder="e.g. Austin Murray" isMobile={isMobile} icon={Icons.user} />
          <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />
          <WizardInput label="Street Address" value={restaurant.address} onChange={v => setField('address', v)} placeholder="e.g. 123 Main Street" isMobile={isMobile} icon={Icons.mapPin} />
          <div style={{ display: 'flex', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
            <div style={{ flex: 2 }}><WizardInput label="City" value={restaurant.city} onChange={v => setField('city', v)} placeholder="Kansas City" isMobile={isMobile} /></div>
            <div style={{ flex: 1 }}><WizardInput label="State" value={restaurant.state} onChange={v => setField('state', v)} placeholder="MO" isMobile={isMobile} /></div>
            <div style={{ flex: 1 }}><WizardInput label="Zip" value={restaurant.zip} onChange={v => setField('zip', v)} placeholder="64108" isMobile={isMobile} /></div>
          </div>
          <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />
          <WizardPhoneInput value={restaurant.phone} onChange={v => setField('phone', v)} isMobile={isMobile} />
          <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />
          <WizardInput label="Manager Cell (for SMS & invoice texts)" value={restaurant.manager_phone} onChange={v => setField('manager_phone', v)} placeholder="(555) 123-4567" isMobile={isMobile} icon={Icons.phone} />
          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Text invoice photos to your Posted number to auto-parse them</div>
          <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />
          <RestaurantTypePicker value={restaurant.restaurant_type} onChange={v => setField('restaurant_type', v)} />
        </div>
      </div>

      {/* Labor target */}
      <div style={{ background: '#FFF', borderRadius: 16, padding: isMobile ? '16px 18px' : '20px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #EBEBEB', marginTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, fontFamily: "'DM Sans',sans-serif" }}>Labor Cost Target</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <input type="range" min="15" max="35" step="1" value={targetLabor}
            onChange={e => setTargetLabor(parseInt(e.target.value))} style={{ flex: 1 }} />
          <span style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", minWidth: 50, textAlign: 'right' }}>{targetLabor}%</span>
        </div>
        <div style={{ fontSize: 12, color: '#BBB', marginTop: 6, fontFamily: "'DM Sans',sans-serif" }}>Most restaurants target 20–25%.</div>
      </div>
    </div>
  );
}

/* ── Restaurant type picker ── */
const RESTAURANT_TYPES = [
  { key: 'full_service', label: 'Full service' },
  { key: 'fast_casual', label: 'Fast casual' },
  { key: 'quick_service', label: 'Quick service' },
  { key: 'fine_dining', label: 'Fine dining' },
  { key: 'buffet', label: 'Buffet' },
  { key: 'cafe_bakery', label: 'Café / bakery' },
  { key: 'bar_gastropub', label: 'Bar / gastropub' },
  { key: 'food_truck', label: 'Food truck' },
  { key: 'catering', label: 'Catering' },
];

function RestaurantTypePicker({ value, onChange }) {
  const known = RESTAURANT_TYPES.some(t => t.key === value);
  const [custom, setCustom] = useState(!known && value ? value : '');
  const isOther = value && !known;
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontFamily: "'DM Sans',sans-serif" }}>
        Type of restaurant
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {RESTAURANT_TYPES.map(t => {
          const active = value === t.key;
          return (
            <button key={t.key} type="button" onClick={() => onChange(t.key)}
              style={{
                padding: '8px 12px', borderRadius: 10, fontSize: 13, fontWeight: 500,
                fontFamily: "'DM Sans',sans-serif", cursor: 'pointer',
                border: active ? '2px solid #1a1a1a' : '2px solid #E5E5E5',
                background: active ? '#1a1a1a' : '#FAFAFA',
                color: active ? '#FFF' : '#666',
                transition: 'all 0.15s ease',
              }}>
              {t.label}
            </button>
          );
        })}
        <button type="button" onClick={() => onChange(custom || 'other')}
          style={{
            padding: '8px 12px', borderRadius: 10, fontSize: 13, fontWeight: 500,
            fontFamily: "'DM Sans',sans-serif", cursor: 'pointer',
            border: isOther ? '2px solid #1a1a1a' : '2px solid #E5E5E5',
            background: isOther ? '#1a1a1a' : '#FAFAFA',
            color: isOther ? '#FFF' : '#666',
            transition: 'all 0.15s ease',
          }}>
          Other
        </button>
      </div>
      {isOther && (
        <input
          type="text"
          value={custom}
          onChange={e => { setCustom(e.target.value); onChange(e.target.value); }}
          placeholder="Describe your format (e.g. ghost kitchen, drive-thru only)"
          style={{ width: '100%', marginTop: 10, padding: '10px 14px', borderRadius: 10, border: '2px solid #E5E5E5', background: '#FAFAFA', fontSize: 14, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", outline: 'none', boxSizing: 'border-box' }}
        />
      )}
      <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>Used by Menu Coach so dish suggestions fit your service format.</div>
    </div>
  );
}

/* ── Wizard-style Input ── */
function WizardInput({ label, value, onChange, placeholder, isMobile, icon }) {
  const [focused, setFocused] = useState(false);
  const filled = value && value.length > 0;
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: focused ? '#1a1a1a' : '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, fontFamily: "'DM Sans',sans-serif", transition: 'color 0.2s ease' }}>
        {icon && <span style={{ color: focused ? '#1a1a1a' : '#CCC', transition: 'color 0.2s ease', display: 'flex' }}>{icon}</span>}{label}
      </label>
      <div style={{ position: 'relative' }}>
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ width: '100%', padding: isMobile ? '14px 16px' : '12px 16px', paddingRight: filled ? 44 : 16, borderRadius: 12, border: focused ? '2px solid #1a1a1a' : filled ? '2px solid #2D8F5E40' : '2px solid #E5E5E5', background: focused ? '#FFF' : '#FAFAFA', fontSize: isMobile ? 16 : 15, fontWeight: 500, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box', WebkitAppearance: 'none' }} />
        {filled && !focused && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, borderRadius: '50%', background: '#2D8F5E', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF' }}>{Icons.check}</div>}
      </div>
    </div>
  );
}

/* ── Wizard-style Phone Input ── */
function WizardPhoneInput({ value, onChange, isMobile }) {
  const [focused, setFocused] = useState(false);
  const filled = value && value.length > 0;
  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: focused ? '#1a1a1a' : '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, fontFamily: "'DM Sans',sans-serif", transition: 'color 0.2s ease' }}>
        <span style={{ color: focused ? '#1a1a1a' : '#CCC', transition: 'color 0.2s ease', display: 'flex' }}>{Icons.phone}</span>Restaurant Phone
      </label>
      <div style={{ position: 'relative' }}>
        <input type="tel" value={value || ''} onChange={e => onChange(formatPhone(e.target.value))} placeholder="(555) 123-4567"
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ width: '100%', padding: isMobile ? '14px 16px' : '12px 16px', paddingRight: filled ? 44 : 16, borderRadius: 12, border: focused ? '2px solid #1a1a1a' : filled ? '2px solid #2D8F5E40' : '2px solid #E5E5E5', background: focused ? '#FFF' : '#FAFAFA', fontSize: isMobile ? 16 : 15, fontWeight: 500, color: '#1a1a1a', fontFamily: "'DM Sans',sans-serif", outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box', WebkitAppearance: 'none' }} />
        {filled && !focused && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, borderRadius: '50%', background: '#2D8F5E', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFF' }}>{Icons.check}</div>}
      </div>
    </div>
  );
}

/* ── Email forwarding tab ──────────────────────────────────────────────────
 *
 * Operators forward vendor invoice emails to parse@invoices.tryposted.app.
 * Posted's Postmark Inbound webhook stages each attachment to Supabase
 * storage; the browser-side drain consumer (FoodPriceTracker) then runs
 * them through the existing parser. Operators don't have to do anything
 * but forward — Posted does the rest.
 *
 * This tab gives operators three things:
 *   1. The forward address itself (copy-to-clipboard).
 *   2. Authorized senders CRUD — only emails from these addresses get
 *      ingested. Backfilled from companies.owner_email.
 *   3. Recent activity log — last 20 inbound_emails rows for this
 *      company, with status chips. Click to expand and see subject /
 *      attachments / parsed-invoice link.
 *
 * Visual treatment matches the rest of Settings (DM Sans, soft borders,
 * 12-radius cards). See DESIGN_SYSTEM.md §Email-inbound source for the
 * provenance convention.
 */
const FORWARD_ADDRESS_FALLBACK = 'parse@invoices.tryposted.app';
const MAX_AUTHORIZED_SENDERS = 25;
const STATUS_PALETTE = {
  parsed: { bg: '#E8F5EE', fg: '#1F7A48', label: 'parsed' },
  parsing: { bg: '#FFF7E0', fg: '#7A5A00', label: 'parsing\u2026' },
  received: { bg: '#FFF7E0', fg: '#7A5A00', label: 'staging\u2026' },
  parse_failed: { bg: '#FBE5E5', fg: '#9B1C1C', label: 'parse failed' },
  unauthorized_sender: { bg: '#FBE5E5', fg: '#9B1C1C', label: 'unauthorized sender' },
  no_attachments: { bg: '#FFF7E0', fg: '#7A5A00', label: 'no attachments' },
  unsupported_format: { bg: '#FFF7E0', fg: '#7A5A00', label: 'unsupported format' },
};

function EmailForwardingTab({ companyId, isMobile }) {
  const forwardAddress = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_POSTED_INBOUND_ADDRESS)
    || FORWARD_ADDRESS_FALLBACK;
  const [senders, setSenders] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newSender, setNewSender] = useState('');
  const [adding, setAdding] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [expandedActivityId, setExpandedActivityId] = useState(null);

  // Re-fetch on mount + after every mutation so the table reflects truth.
  const fetchSenders = React.useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('authorized_email_senders')
      .select('*')
      .eq('company_id', companyId)
      .order('added_at', { ascending: true });
    setSenders(data || []);
  }, [companyId]);

  const fetchActivity = React.useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('inbound_emails')
      .select('id, from_address, subject, status, attachment_count, received_at, invoice_ids, error_message')
      .eq('company_id', companyId)
      .order('received_at', { ascending: false })
      .limit(20);
    setActivity(data || []);
  }, [companyId]);

  useEffect(() => { fetchSenders(); }, [fetchSenders]);
  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // Keep the activity table fresh as inbound rows arrive — same realtime
  // channel filter pattern as the App.js drain subscription, scoped to
  // this Settings tab so we don't carry the listener around when the
  // operator is elsewhere.
  useEffect(() => {
    if (!companyId) return;
    const channel = supabase
      .channel(`settings-inbound-${companyId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'inbound_emails',
        filter: `company_id=eq.${companyId}`,
      }, () => { fetchActivity(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [companyId, fetchActivity]);

  function handleCopy() {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(forwardAddress).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }

  async function handleAdd() {
    setErrorMsg('');
    const email = newSender.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg('That doesn\'t look like a valid email address.');
      return;
    }
    if (senders.some(s => String(s.email_address || '').toLowerCase() === email)) {
      setErrorMsg('Already on the list.');
      return;
    }
    if (senders.length >= MAX_AUTHORIZED_SENDERS) {
      setErrorMsg(`Cap reached (${MAX_AUTHORIZED_SENDERS} senders). Remove one to add another.`);
      return;
    }
    setAdding(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const addedBy = userData?.user?.id || null;
      const { error } = await supabase.from('authorized_email_senders').insert({
        company_id: companyId,
        email_address: email,
        added_by: addedBy,
      });
      if (error) {
        setErrorMsg(error.message);
      } else {
        setNewSender('');
        await fetchSenders();
      }
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(id) {
    await supabase.from('authorized_email_senders').delete().eq('id', id);
    await fetchSenders();
  }

  const cardStyle = {
    background: '#FFF', borderRadius: 14, padding: isMobile ? 18 : 22,
    border: '1px solid #EDEDED', marginBottom: 16,
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em' };

  return (
    <div style={{ animation: 'fadeUp 0.25s ease' }}>
      {/* 1. Forward address */}
      <div style={cardStyle}>
        <div style={labelStyle}>Forward invoices to</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
          <code style={{
            flex: 1, minWidth: 240,
            padding: '10px 14px', borderRadius: 10,
            background: '#F5F5F4', border: '1px solid #E5E5E5',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 14, color: '#1a1a1a',
          }}>{forwardAddress}</code>
          <button
            onClick={handleCopy}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: copied ? '#2D8F5E' : '#1a1a1a', color: '#FFF',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {copied ? '\u2713 Copied' : 'Copy'}
          </button>
        </div>
        <p style={{ fontSize: 13, color: '#666', marginTop: 12, lineHeight: 1.5 }}>
          Forward any invoice email to this address from an authorized sender below and Posted will parse it
          automatically. Most operators set up a forward rule from their inbox so vendor invoices land in
          Posted without any manual work.
        </p>
      </div>

      {/* 2. Authorized senders */}
      <div style={cardStyle}>
        <div style={labelStyle}>Authorized senders</div>
        <p style={{ fontSize: 13, color: '#666', margin: '6px 0 14px' }}>
          Only emails forwarded from these addresses will be ingested. Add new addresses for AP staff or
          managers who don't have Posted accounts.
        </p>

        {senders.length === 0 ? (
          <div style={{ fontSize: 13, color: '#999', padding: '10px 0' }}>
            No authorized senders yet. Add one below.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {senders.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10,
                background: '#FAFAFA', border: '1px solid #EDEDED',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>{s.email_address}</div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                    Added {s.added_at ? new Date(s.added_at).toLocaleDateString() : ''}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(s.id)}
                  style={{
                    padding: '6px 10px', borderRadius: 8, border: '1px solid #E5E5E5',
                    background: '#FFF', color: '#666', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add sender input */}
        <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="email"
            value={newSender}
            onChange={e => { setNewSender(e.target.value); if (errorMsg) setErrorMsg(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="ap-clerk@yourrestaurant.com"
            style={{
              flex: 1, minWidth: 220,
              padding: '10px 14px', borderRadius: 10,
              border: '1px solid #E5E5E5', background: '#FFF',
              fontSize: 14, color: '#1a1a1a',
              fontFamily: "'DM Sans',sans-serif",
              outline: 'none',
            }}
          />
          <button
            onClick={handleAdd}
            disabled={adding || !newSender.trim()}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: '#1a1a1a', color: '#FFF',
              fontSize: 13, fontWeight: 600,
              cursor: adding || !newSender.trim() ? 'not-allowed' : 'pointer',
              opacity: adding || !newSender.trim() ? 0.5 : 1,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {adding ? 'Adding\u2026' : 'Add'}
          </button>
        </div>
        {errorMsg && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#9B1C1C' }}>{errorMsg}</div>
        )}
        <div style={{ marginTop: 8, fontSize: 11, color: '#AAA' }}>
          {senders.length} of {MAX_AUTHORIZED_SENDERS} senders.
        </div>
      </div>

      {/* 3. Recent activity */}
      <div style={cardStyle}>
        <div style={labelStyle}>Recent activity</div>
        <p style={{ fontSize: 13, color: '#666', margin: '6px 0 14px' }}>
          Last 20 emails received. Click a row to expand.
        </p>

        {activity.length === 0 ? (
          <div style={{ fontSize: 13, color: '#999', padding: '10px 0' }}>
            No emails received yet. Once you forward your first invoice it'll show up here.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activity.map(row => {
              const palette = STATUS_PALETTE[row.status] || { bg: '#EEE', fg: '#666', label: row.status };
              const isExpanded = expandedActivityId === row.id;
              return (
                <div key={row.id} style={{
                  borderRadius: 10, border: '1px solid #EDEDED',
                  background: isExpanded ? '#FAFAFA' : '#FFF',
                }}>
                  <button
                    onClick={() => setExpandedActivityId(isExpanded ? null : row.id)}
                    style={{
                      width: '100%', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', border: 'none', background: 'transparent',
                      cursor: 'pointer', fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.from_address || '(no sender)'}
                      </div>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                        {row.received_at ? new Date(row.received_at).toLocaleString() : ''}
                        {row.attachment_count > 0 && ` \u00B7 ${row.attachment_count} attachment${row.attachment_count === 1 ? '' : 's'}`}
                      </div>
                    </div>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
                      background: palette.bg, color: palette.fg,
                      fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
                      whiteSpace: 'nowrap',
                    }}>
                      {palette.label}
                    </span>
                  </button>
                  {isExpanded && (
                    <div style={{ padding: '0 14px 14px', borderTop: '1px solid #EDEDED', fontSize: 13, color: '#444', lineHeight: 1.5 }}>
                      <div style={{ marginTop: 10 }}>
                        <span style={{ color: '#999' }}>Subject:</span>{' '}
                        <span style={{ color: '#1a1a1a' }}>{row.subject || '(no subject)'}</span>
                      </div>
                      {Array.isArray(row.invoice_ids) && row.invoice_ids.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <span style={{ color: '#999' }}>Invoices created:</span>{' '}
                          {row.invoice_ids.map((id, i) => (
                            <button
                              key={id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (typeof window !== 'undefined') {
                                  window.dispatchEvent(new CustomEvent('app:open-invoice-review', { detail: { invoiceId: id } }));
                                }
                              }}
                              style={{
                                background: 'transparent', border: 'none',
                                color: '#1F7A48', textDecoration: 'underline',
                                cursor: 'pointer', fontSize: 13,
                                padding: 0, marginRight: 8,
                                fontFamily: "'DM Sans',sans-serif",
                              }}
                            >
                              #{id}{i < row.invoice_ids.length - 1 ? ',' : ''}
                            </button>
                          ))}
                        </div>
                      )}
                      {row.error_message && (
                        <div style={{ marginTop: 6, color: '#9B1C1C' }}>
                          <span style={{ color: '#999' }}>Error:</span> {row.error_message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
